// Curated chat-color palette (owner ask: "different chat colors per LLM,
// selectable in Settings"; decision: per PROVIDER, not per model — see
// BotAnswer.provider / DEFAULT_PROVIDER_SWATCH below). Consumed by
// AnswerCard.tsx (dot + card tint) and SettingsTab.tsx (the swatch picker).
// Stored server-side as bot.UserSetting.provider_colors, a JSON
// { [provider]: swatchId } object (migration 264) — swatchId here IS that
// stored value, so BOT_SWATCHES ids must stay ASCII-lowercase-kebab and
// ≤ 24 chars to match functions/bot.js's SWATCH_ID_RE guard.
//
// `border`/`bg` are `dot` at fixed alpha (RRGGBBAA hex — no oklch/color-mix
// dependency): 35% for the card border, 20% for the card background (7% was
// invisible on white in practice — owner feedback 2026-09-01: the selected
// color should visibly read as the card surface). Because
// these are genuinely translucent (not opaque theme-specific colors), they
// sit correctly over both the light and dark `surface-container-lowest`
// backdrop without a separate .dark set — DESIGN.md's flat data-surface
// pattern (bg-surface-container-lowest + border-outline-variant, the same
// pair AnswerCard/ReportTab already use) just shows through underneath.

export interface BotSwatch {
  id: string
  /** Solid mid-tone — used for the little status dot. */
  dot: string
  /** `dot` at ~35% alpha — full card border tint (never a side stripe). */
  border: string
  /** `dot` at ~20% alpha — clearly visible card background tint. */
  bg: string
}

function swatch(id: string, dot: string): BotSwatch {
  return { id, dot, border: `${dot}59`, bg: `${dot}33` }
}

// 8 curated, chroma-modest, no-neon tones — kept restrained (700-ish
// weights) since these tint whole cards, not just small chips.
export const BOT_SWATCHES: BotSwatch[] = [
  swatch('slate', '#64748b'),
  swatch('amber', '#b45309'),
  swatch('teal', '#0f766e'),
  swatch('violet', '#6d28d9'),
  swatch('rose', '#be123c'),
  swatch('indigo', '#4338ca'),
  swatch('olive', '#6b7d33'),
  swatch('cyan', '#0e7490'),
]

const NEUTRAL_SWATCH = BOT_SWATCHES[0] // slate

/** Distinct out-of-the-box colors so the three providers never collide
 *  before a user has touched Settings › Chat-Farben. */
export const DEFAULT_PROVIDER_SWATCH: Record<'anthropic' | 'openai' | 'google', string> = {
  anthropic: 'amber',
  openai: 'teal',
  google: 'indigo',
}

export function swatchFor(id?: string): BotSwatch {
  return BOT_SWATCHES.find((s) => s.id === id) ?? NEUTRAL_SWATCH
}
